import React, { useCallback, useEffect, useState } from "react";
import { Hammer, Send, CalendarDays, Gauge, Zap, HardHat, Palette, Ruler, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import EnvoiPoseFlowModal from "./EnvoiPoseFlowModal";

const fmtDate = (v) => (v ? new Date(`${String(v).slice(0, 10)}T12:00:00`).toLocaleDateString("fr-FR") : null);
const num = (v, unit) => (v === null || v === undefined || v === "" ? null : `${Number(v).toLocaleString("fr-FR")} ${unit}`);

function rowsFor(produit, v, sousTraitant) {
  const st = { id: "poseur", type: "text", icon: HardHat, label: "Sous-traitant", value: v.poseur || sousTraitant, raw: v.poseur || "" };
  const date = { id: "date_pose", type: "datetime-local", icon: CalendarDays, label: "Date de pose", value: fmtDate(v.date_pose), raw: (v.date_pose || "").slice(0, 16) };
  if ((produit || "").toUpperCase() === "ITE") {
    return [{ id: v.surface_finale_ite !== undefined || !v.metrage ? "surface_finale_ite" : "metrage", type: "number", icon: Ruler, label: "Surface", value: num(v.surface_finale_ite ?? v.metrage, "m²"), raw: v.surface_finale_ite ?? v.metrage ?? "" }, date,
      { id: "couleur_crepi", type: "text", icon: Palette, label: "Couleur crépi", value: v.couleur_crepi, raw: v.couleur_crepi || "" }, st];
  }
  const isPac = (produit || "").toUpperCase().startsWith("PAC");
  const pk = isPac ? "puissance_kw" : "puissance_kwc";
  return [{ id: pk, type: "number", icon: Gauge, label: "Puissance", value: num(v[pk], isPac ? "kW" : "kWc"), raw: v[pk] ?? "" }, date,
    { id: "type_compteur", type: "select", options: ["Monophasé", "Triphasé"], icon: Zap, label: "Compteur", value: v.type_compteur, raw: v.type_compteur || "" }, st];
}

// Même donnée que l'onglet Installation (aucun doublon) : l'enregistrement écrit dans l'onglet Installation.
function EditCell({ r, dossierId, onSaved }) {
  const [edit, setEdit] = useState(false);
  const [v, setV] = useState(r.raw);
  const save = async () => {
    try {
      await api.patch(`/dossiers/${dossierId}/tab-data/installation`, { values: { [r.id]: r.type === "number" ? (v === "" ? null : Number(v)) : v || null } });
      toast.success(`${r.label} enregistré (onglet Installation)`); setEdit(false); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Enregistrement impossible"); }
  };
  if (!edit) return (
    <button onClick={() => { setV(r.raw); setEdit(true); }} className={`text-sm mt-0.5 text-left hover:underline decoration-dotted ${r.value ? "text-norea-text font-medium" : "text-norea-textDisabled italic"}`} title="Modifier" data-testid={`install-edit-${r.id}`}>{r.value || "À renseigner"}</button>
  );
  const cls = "input-field !py-1 text-xs";
  return (
    <div className="flex items-center gap-1 mt-0.5">
      {r.type === "select" ? <select value={v} onChange={(e) => setV(e.target.value)} className={cls} data-testid={`install-input-${r.id}`}><option value="">—</option>{r.options.map((o) => <option key={o}>{o}</option>)}</select>
        : <input type={r.type} value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => e.key === "Enter" && save()} className={cls} autoFocus data-testid={`install-input-${r.id}`} />}
      <button onClick={save} className="btn-primary !py-1 !px-2 text-xs" data-testid={`install-save-${r.id}`}>OK</button>
      <button onClick={() => setEdit(false)} className="btn-ghost !p-1 text-xs">✕</button>
    </div>
  );
}

export default function InstallationSyntheseCard({ dossier }) {
  const [values, setValues] = useState(null);
  const [open, setOpen] = useState(false);
  const [sentNow, setSentNow] = useState({});
  const load = useCallback(() => api.get(`/dossiers/${dossier.id}/tab-data/installation`).then((r) => setValues(r.data.values || {})).catch(() => setValues({})), [dossier.id]);
  useEffect(() => { load(); }, [load]);
  if (!values) return null;
  const rows = rowsFor(dossier.produit, values, dossier.sous_traitant);
  const ep = { ...(dossier.envoi_pose || {}), ...sentNow };
  const last = ep.email_sent_at || ep.whatsapp_sent_at;
  return (
    <div className="card-flat p-5" data-testid="installation-synthese-card">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="overline flex items-center gap-1.5"><Hammer className="w-3 h-3" /> Installation {dossier.produit ? `· ${dossier.produit}` : ""}</div>
        <button onClick={() => setOpen(true)} className="btn-primary text-sm" data-testid="envoi-pose-btn"><Send className="w-4 h-4" /> Envoi en pose</button>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {rows.map((r) => (
          <div key={r.label} data-testid={`install-summary-${r.label}`}>
            <div className="text-[11px] text-norea-textMuted flex items-center gap-1"><r.icon className="w-3 h-3" /> {r.label}</div>
            <EditCell r={r} dossierId={dossier.id} onSaved={() => { load(); window.dispatchEvent(new CustomEvent("norea:rdv-changed")); }} />
          </div>
        ))}
      </div>
      {last && (
        <div className="mt-3 text-[11px] text-emerald-700 flex items-center gap-1" data-testid="envoi-pose-last">
          <CheckCircle2 className="w-3 h-3" /> Envoyé en pose le {new Date(last).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}
          {ep.email_sent_at && " · email"}{ep.whatsapp_sent_at && " · WhatsApp"}
        </div>
      )}
      {open && <EnvoiPoseFlowModal dossier={dossier} onSent={() => setSentNow((x) => ({ ...x, email_sent_at: x.email_sent_at || new Date().toISOString() }))} onClose={() => { setOpen(false); load(); }} />}
    </div>
  );
}
